import { Component, OnInit, TemplateRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import {
  CaisseOption,
  LogicielCaisseVendeurService,
  VendeurErp,
} from 'app/services/logiciel-caisse/logiciel-caisse-vendeur.service';
import { LogicielCaisseCaisseService } from 'app/services/logiciel-caisse/logiciel-caisse-caisse.service';

@Component({
  selector: 'app-logiciel-caisse-vendeur-caisse',
  templateUrl: './logiciel-caisse-vendeur-caisse.component.html',
  styleUrls: ['./logiciel-caisse-vendeur-caisse.component.scss'],
})
export class LogicielCaisseVendeurCaisseComponent implements OnInit {
  loading = false;
  saving = false;
  rows: VendeurErp[] = [];
  filteredRows: VendeurErp[] = [];
  searchTerm = '';
  editId: number | null = null;
  form: UntypedFormGroup;

  caisseOptions: CaisseOption[] = [];
  caisseIdsByVendeur = new Map<number, string[]>();
  caisseLabelsByVendeur = new Map<number, string[]>();

  associationRow: VendeurErp | null = null;
  associationCaisseIds: string[] = [];
  associationSaving = false;

  constructor(
    private readonly vendeurService: LogicielCaisseVendeurService,
    private readonly caisseService: LogicielCaisseCaisseService,
    private readonly modalService: NgbModal,
    private readonly fb: UntypedFormBuilder,
    private readonly toastr: ToastrService
  ) {
    this.form = this.fb.group({
      identifiant: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
      nom: ['', Validators.required],
      prenom: ['', Validators.required],
      telephone: [''],
      password: [''],
    });
  }

  ngOnInit(): void {
    this.load();
  }

  vendeurLabel(row: VendeurErp): string {
    const name = `${row.prenom ?? ''} ${row.nom ?? ''}`.trim();
    return name || String(row.identifiant ?? '');
  }

  load(): void {
    this.loading = true;
    forkJoin({
          caisses: this.caisseService.getCaissesTableRows(),
          vendeurs: this.vendeurService.listErp(),
          links: this.vendeurService.listCaisseVendeurLinks(),
        }).subscribe({
        next: ({ caisses, vendeurs, links }) => {
          this.caisseOptions = caisses.map((c) => ({
            id: String(c.id),
            label: [c.CAISSE, c.ENTREPOT !== '—' ? c.ENTREPOT : null].filter(Boolean).join(' — '),
          }));
          this.rows = vendeurs;
          this.applySearch();
          const idsMap = new Map<number, string[]>();
          const labelsMap = new Map<number, string[]>();
          for (const link of links) {
            const vid = Number(link.vendeur_id);
            const cid = String(link.caisse_id);
            const idList = idsMap.get(vid) ?? [];
            idList.push(cid);
            idsMap.set(vid, idList);

            const opt = this.caisseOptions.find((o) => o.id === cid);
            const label =
              opt?.label ||
              (link.numero_de_caisse != null
                ? `Caisse ${link.numero_de_caisse}`
                : `Caisse ${link.caisse_id}`);
            const labelList = labelsMap.get(vid) ?? [];
            labelList.push(label);
            labelsMap.set(vid, labelList);
          }
          this.caisseIdsByVendeur = idsMap;
          this.caisseLabelsByVendeur = labelsMap;
          this.loading = false;
          this.refreshFeatherIcons();
        },
        error: (err: Error) => {
          this.toastr.error(err.message);
          this.loading = false;
        },
      });
  }

  private loadCaisseLinks(): void {
    this.vendeurService.listCaisseVendeurLinks().subscribe({
      next: (links) => {
        const idsMap = new Map<number, string[]>();
        const labelsMap = new Map<number, string[]>();
        for (const link of links) {
          const vid = Number(link.vendeur_id);
          const cid = String(link.caisse_id);
          const idList = idsMap.get(vid) ?? [];
          idList.push(cid);
          idsMap.set(vid, idList);

          const opt = this.caisseOptions.find((o) => o.id === cid);
          const label =
            opt?.label ||
            (link.numero_de_caisse != null
              ? `Caisse ${link.numero_de_caisse}`
              : `Caisse ${link.caisse_id}`);
          const labelList = labelsMap.get(vid) ?? [];
          labelList.push(label);
          labelsMap.set(vid, labelList);
        }
        this.caisseIdsByVendeur = idsMap;
        this.caisseLabelsByVendeur = labelsMap;
        this.loading = false;
        this.refreshFeatherIcons();
      },
      error: (err: Error) => {
        this.toastr.error(err.message);
        this.loading = false;
      },
    });
  }

  caissesSummary(row: VendeurErp): string {
    const labels = this.caisseLabelsByVendeur.get(row.id);
    if (!labels?.length) {
      return '—';
    }
    return labels.join(', ');
  }

  applySearch(): void {
    const kw = this.searchTerm.trim().toLowerCase();
    if (!kw) {
      this.filteredRows = [...this.rows];
    } else {
      this.filteredRows = this.rows.filter((row) => {
        const blob = `${this.vendeurLabel(row)} ${row.identifiant} ${row.telephone ?? ''}`.toLowerCase();
        return blob.includes(kw);
      });
    }
    this.refreshFeatherIcons();
  }

  private setPasswordValidators(required: boolean): void {
    const control = this.form.get('password');
    if (!control) {
      return;
    }
    if (required) {
      control.setValidators([Validators.required, Validators.pattern(/^\d{6,}$/)]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity();
  }

  openCreate(modal: TemplateRef<unknown>): void {
    this.editId = null;
    this.form.reset();
    this.setPasswordValidators(true);
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  openEdit(row: VendeurErp, modal: TemplateRef<unknown>): void {
    this.editId = row.id;
    this.form.patchValue({
      identifiant: row.identifiant,
      nom: row.nom,
      prenom: row.prenom,
      telephone: row.telephone ?? '',
      password: '',
    });
    this.setPasswordValidators(false);
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  openAssociation(row: VendeurErp, modal: TemplateRef<unknown>): void {
    this.associationRow = row;
    this.associationCaisseIds = [...(this.caisseIdsByVendeur.get(row.id) ?? [])];
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  save(modalRef: { close: () => void }): void {
    if (this.form.invalid) {
      return;
    }
    const body = {
      identifiant: String(this.form.value.identifiant).trim(),
      nom: String(this.form.value.nom).trim(),
      prenom: String(this.form.value.prenom).trim(),
      telephone: String(this.form.value.telephone ?? '').trim() || null,
    };
    const password = String(this.form.value.password ?? '').trim();

    if (password && !/^\d{6,}$/.test(password)) {
      this.toastr.error('Le code PIN doit contenir au moins 6 chiffres.');
      return;
    }

    this.saving = true;

    if (this.editId) {
      const updateBody: Record<string, string | null> = { ...body };
      if (password) {
        updateBody.password = password;
      }
      this.vendeurService.updateErp(this.editId, updateBody).subscribe({
        next: () => {
          this.saving = false;
          modalRef.close();
          this.toastr.success('Vendeur enregistré.');
          this.load();
        },
        error: (err: Error) => {
          this.saving = false;
          this.toastr.error(err.message);
        },
      });
      return;
    }

    if (!password) {
      this.saving = false;
      this.toastr.error('Le code PIN est requis.');
      return;
    }

    this.vendeurService.createErp({ ...body, password }).subscribe({
      next: () => {
        this.saving = false;
        modalRef.close();
        this.toastr.success('Vendeur créé.');
        this.load();
      },
      error: (err: Error) => {
        this.saving = false;
        this.toastr.error(err.message);
      },
    });
  }

  deleteRow(row: VendeurErp): void {
    if (!confirm(`Supprimer le vendeur « ${this.vendeurLabel(row)} » ?`)) {
      return;
    }
    this.vendeurService.deleteErp(row.id).subscribe({
      next: () => {
        this.toastr.success('Vendeur supprimé.');
        this.load();
      },
      error: (err: Error) => {
        this.toastr.error(err.message);
      },
    });
  }

  saveAssociation(modalRef: { close: () => void }): void {
    if (!this.associationRow) {
      return;
    }
    this.associationSaving = true;
    const ids = this.associationCaisseIds
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);
    this.vendeurService.setVendeurCaisses(this.associationRow.id, ids).subscribe({
      next: () => {
        this.associationSaving = false;
        modalRef.close();
        this.associationRow = null;
        this.toastr.success('Association enregistrée.', 'Vendeur');
        this.loadCaisseLinks();
      },
      error: (err: Error) => {
        this.associationSaving = false;
        this.toastr.error(err.message);
      },
    });
  }

  private refreshFeatherIcons(): void {
    setTimeout(() => {
      if (typeof (window as unknown as { feather?: { replace: () => void } }).feather?.replace === 'function') {
        (window as unknown as { feather: { replace: () => void } }).feather.replace();
      }
    }, 0);
  }
}
